"use client";

import React, { useEffect, useState } from "react";
import { ArrowLeft, FileCheck2, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface LeaseAssessmentIndASProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface LeaseRow {
  id: string;
  lease_name: string;
  commencement_date: string;
  lease_period: number;
  int_ror: number;
}

const FILE_NAME = "Execution_LEASE_AssessmentInp.json";

export default function LeaseAssessmentIndAS({ setActiveSection }: LeaseAssessmentIndASProps) {
  const [leases, setLeases] = useState<LeaseRow[]>([]);
  const [existingPayload, setExistingPayload] = useState<any>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(FILE_NAME);
        if (result?.success && result?.data?.leases) {
          setExistingPayload(result.data);
          const loadedLeases: LeaseRow[] = result.data.leases.map((lease: any, index: number) => ({
            id: `lease_${Date.now()}_${index}`,
            lease_name: lease.lease_name || "",
            commencement_date: toInputDate(lease.commencement_date || ""),
            lease_period: Number(lease.lease_period) || 0,
            int_ror: Number(lease.int_ror) || 0,
          }));
          setLeases(loadedLeases);
        }
      } catch (error) {
        console.warn("[LeaseAssessmentIndAS] Failed to load data:", error);
      }
    };
    loadData();
  }, []);

  const addLease = () => {
    setLeases((prev) => [
      ...prev,
      {
        id: `lease_${Date.now()}`,
        lease_name: "",
        commencement_date: "",
        lease_period: 0,
        int_ror: 0,
      },
    ]);
  };

  const removeLease = (id: string) => setLeases((prev) => prev.filter((lease) => lease.id !== id));

  const updateLease = (id: string, field: keyof LeaseRow, value: string | number) => {
    setLeases((prev) => prev.map((lease) => (lease.id === id ? { ...lease, [field]: value } : lease)));
  };

  const saveData = async () => {
    if (saveStatus === "saving") return;
    setSaveStatus("saving");
    try {
      const preservedByName = new Map<string, any>();
      const existingLeases = Array.isArray(existingPayload?.leases) ? existingPayload.leases : [];
      existingLeases.forEach((lease: any) => {
        if (lease?.lease_name) preservedByName.set(lease.lease_name, lease);
      });

      const payload = {
        leases: leases.map((lease) => {
          const existingLease = preservedByName.get(lease.lease_name);
          return {
            lease_name: lease.lease_name || "",
            lease_type: existingLease?.lease_type || "",
            commencement_date: lease.commencement_date ? fromInputDate(lease.commencement_date) : "",
            lease_period: Number(lease.lease_period) || 0,
            int_ror: Number(lease.int_ror) || 0,
            payment_cycle: existingLease?.payment_cycle || "",
            conditions: existingLease?.conditions || {
              c_i: "No",
              c_ii: "No",
              c_iii: "No",
              c_iv: "No",
              c_v: "No",
            },
            payment_details: Array.isArray(existingLease?.payment_details) ? existingLease.payment_details : [],
          };
        }),
      };

      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured. Please select an engagement.");

      const base64Content = btoa(JSON.stringify(payload, null, 2));
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: FILE_NAME,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_LEASE_AssessmentInp"],
      });

      if (!result?.success) throw new Error(result?.error || "Failed to save file");
      setExistingPayload(payload);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("[LeaseAssessmentIndAS] Failed to save data:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Assessment</h2>
          <p className="text-gray-400">Add one or more leases with core lease inputs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={saveData} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saveStatus === "saving"}>
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          {saveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
          <Button variant="outline" onClick={() => setActiveSection?.("ll-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-blue-500" />
              Lease Assessment
            </div>
            <Button onClick={addLease} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Lease
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {leases.length === 0 ? (
            <div className="text-center py-8 text-white/60">No leases added yet. Click "Add Lease" to get started.</div>
          ) : (
            leases.map((lease, index) => (
              <div key={lease.id} className="border border-white/10 rounded-lg p-4 bg-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-medium">Lease #{index + 1}</h4>
                  <Button onClick={() => removeLease(lease.id)} size="sm" variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Name of Lease</Label>
                    <Input value={lease.lease_name} onChange={(e) => updateLease(lease.id, "lease_name", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Commencement Date</Label>
                    <Input type="date" value={lease.commencement_date} onChange={(e) => updateLease(lease.id, "commencement_date", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Period (in months)</Label>
                    <Input type="number" value={lease.lease_period} onChange={(e) => updateLease(lease.id, "lease_period", parseInt(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Effective Interest Rate p.a.</Label>
                    <Input type="number" value={lease.int_ror} onChange={(e) => updateLease(lease.id, "int_ror", parseFloat(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function toInputDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  const parts = value.includes("/") ? value.split("/") : [];
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0");
    const day = parts[1].padStart(2, "0");
    const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
    return `${year}-${month}-${day}`;
  }
  return "";
}

function fromInputDate(inputDate: string): string {
  if (!inputDate) return "";
  const [year, month, day] = inputDate.split("-");
  return `${month}/${day}/${year}`;
}
